<template>
  <div>
    <div
      v-if="shouldShowFab"
      class="grainfox-fab-container"
    >
      <v-menu
        :value="showNotification && pendingResponseNotification"
        top
        offset-y
        :close-on-click="false"
        :close-on-content-click="false"
        nudge-top="8"
        content-class="sinoa-popup-content"
        @input="setPendingResponseNotification"
      >
        <template #activator="{ attrs }">
          <div
            v-bind="attrs"
            style="position: relative;"
          >
            <div
              v-if="pendingResponseNotification"
              class="sinoa-fab-notification-dot"
            />
            <v-card
              elevation="8"
              class="pa-2 rounded-pill"
              :class="{
                'sinoa-fab--streaming': isStreamingInBackground,
                'sinoa-fab--ready': pendingResponseNotification,
              }"
              :style="{ border: '5px solid ' + fabBorderColor }"
              @click="openAssistantDialog"
            >
              <CyclicPlaceholder
                v-slot="{ text }"
                v-bind="fabCyclicProps"
              >
                <v-text-field
                  v-model="userInput"
                  solo
                  hide-details
                  flat
                  dense
                  readonly
                  class="pointer-events-none"
                  :class="{ 'highlight-fab-text': $isMobile && highlightText }"
                  :style="$isMobile && highlightText ? { '--fab-label-color': COLORS.grainfoxAIButton } : {}"
                  :label="text"
                  append-icon="mdi-arrow-right-circle"
                  v-bind="textFieldProps"
                  @click="openAssistantDialog"
                  @focus="openAssistantDialog"
                >
                  <template #append>
                    <v-icon
                      :color="COLORS.grainfoxAIButton"
                      class="arrow-icon"
                      :size="35"
                      :style="{ transform: 'translateX(10px)'}"
                    >
                      mdi-arrow-right-circle
                    </v-icon>
                  </template>
                  <template #prepend-inner>
                    <img
                      src="/static/dashboard/gif/grainfox-ai.gif"
                      alt="sinoa-logo"
                      class="mr-2 grainfox-logo"
                      :style="{ transform: 'translateX(-10px)' }"
                    >
                  </template>
                </v-text-field>
              </CyclicPlaceholder>
            </v-card>
          </div>
        </template>

        <v-sheet
          class="sinoa-answer-ready-popup d-flex align-center px-4 py-3"
          rounded="xl"
          elevation="4"
          @click="openAssistantDialog"
        >
          <v-icon
            size="16"
            :color="COLORS.grainfoxAIButton"
            class="mr-2 flex-shrink-0"
          >
            mdi-check-circle
          </v-icon>
          <span class="text-caption font-weight-medium flex-grow-1">Sinoa's answer is ready</span>
          <v-btn
            icon
            x-small
            class="ml-2 flex-shrink-0"
            @click.stop="dismissPopup"
          >
            <v-icon size="14">
              mdi-close
            </v-icon>
          </v-btn>
        </v-sheet>
      </v-menu>
    </div>
    <GrainFoxAssistantDialog />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import GrainFoxAssistantDialog from './GrainFoxAssistantDialog.vue';
import { COLORS } from '../../helpers/colors';
import CyclicPlaceholder from './CyclicPlaceholder.vue';
import { datadogRum } from '@datadog/browser-rum';

const SINOA_AUTO_OPEN_KEY = 'sinoa-auto-opened';

export default {
  name: 'FloatingAssistantBtn',

  components: {
    GrainFoxAssistantDialog,
    CyclicPlaceholder,
  },

  props: {
    contextText: {
      type: String,
      default: undefined,
    },

    delegateClick: {
      type: Boolean,
      default: false,
    },

    showNotification: {
      type: Boolean,
      default: true,
    },

    highlightText: {
      type: Boolean,
      default: false,
    },
  },

  async mounted() {
    if (!this.isSinoaInitialized) {
      await this.initializeSinoa();
    }
    this.autoOpenOncePerDay();
  },

  watch: {
    isGrainFoxAssistantDialogOpen(newVal) {
      if (newVal) {
        datadogRum.addAction('Sinoa: Open Chat', {
          source: 'FloatingAssistantBtn',
          entry: 'FAB',
          isMobile: this.$isMobile ?? null,
          isMimic: this.isMimic,
        });
      }
    },
  },

  data() {
    return {
      userInput: null,
      COLORS,
    };
  },

  computed: {
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen', 'isSinoaInitialized', 'pendingResponseNotification']),
    ...mapState('app', ['isMimic']),
    ...mapGetters('chatbot', ['isStreaming']),

    textFieldProps() {
      return (this.$isMobile && this.highlightText)
        ? { color: this.COLORS.grainfoxAIButton }
        : {};
    },

    isFromSinoa() {
      return this.$route?.query?.is_from_sinoa === 'true';
    },

    shouldShowFab() {
      return !this.isGrainFoxAssistantDialogOpen && !this.isFromSinoa;
    },

    isStreamingInBackground() {
      return this.isStreaming && !this.isGrainFoxAssistantDialogOpen;
    },

    fabCyclicProps() {
      if (this.pendingResponseNotification) {
        return { overrideText: 'Tap to View' };
      }
      if (this.isStreamingInBackground) {
        return { overrideText: 'Sinoa is thinking...' };
      }
      return this.contextText ? { overrideText: this.contextText } : {};
    },

    fabBorderColor() {
      if (this.pendingResponseNotification) return '#4CAF50';
      if (this.isStreamingInBackground) return this.COLORS.grainfoxAIButton;
      return this.COLORS.grainfoxAIButtonBorder;
    },
  },

  methods: {
    ...mapActions('chatbot', ['initializeSinoa']),
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen', 'setPendingResponseNotification']),

    dismissPopup() {
      this.setPendingResponseNotification(false);
    },

    openAssistantDialog() {
      this.$emit('click');
      if (this.delegateClick) {
        return;
      }
      this.setGrainFoxAssistantDialogOpen(true);
    },

    autoOpenOncePerDay() {
      if (this.$route?.path !== '/dashboard/') return;
      const today = new Date().toISOString().split('T')[0];
      if (localStorage.getItem(SINOA_AUTO_OPEN_KEY) === today) return;
      localStorage.setItem(SINOA_AUTO_OPEN_KEY, today);
      this.setGrainFoxAssistantDialogOpen(true);
    },
  },
};
</script>

<style scoped>
.grainfox-fab-container {
  position: fixed;
  left: 50%;
  bottom: 5%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 400px;
  z-index: 120;
}

.grainfox-logo {
  height: 40px;
  max-height: 5vh;
  width: auto;
}

@media (max-width: 600px) {
  .grainfox-logo {
    height: 32px;
  }
}

::v-deep .highlight-fab-text .v-label {
  color: var(--fab-label-color) !important;
}

::v-deep .sinoa-popup-content {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
  contain: none !important;
}

::v-deep .sinoa-popup-content .sinoa-answer-ready-popup {
  border: 1.5px solid #12A8C4;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
}

::v-deep .sinoa-popup-content .sinoa-answer-ready-popup::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 10px solid #12A8C4;
}

::v-deep .sinoa-popup-content .sinoa-answer-ready-popup::after {
  content: '';
  position: absolute;
  top: calc(100% - 2px);
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 8px solid white;
}

.sinoa-fab-notification-dot {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 14px;
  height: 14px;
  background: #4CAF50;
  border-radius: 50%;
  border: 2px solid white;
  z-index: 1;
}

@keyframes sinoa-pulse-streaming {
  0% { box-shadow: 0 0 0 0 rgba(18, 168, 196, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(18, 168, 196, 0); }
  100% { box-shadow: 0 0 0 0 rgba(18, 168, 196, 0); }
}

@keyframes sinoa-pulse-ready {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.sinoa-fab--streaming {
  animation: sinoa-pulse-streaming 1.5s infinite;
}

.sinoa-fab--ready {
  animation: sinoa-pulse-ready 1.5s infinite;
}
</style>
